import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";

import { Section, SectionHeader } from "@roqua/components/sections";

import { PageTitle } from "@/components/PageTitle";
import { RenderedDocument } from "@/components/Templating/RenderedDocument";
import { BackButton } from "@/components/ui/BackButton";
import { EmailView } from "@/features/Notifications/EmailView";
import { DeleteNotification } from "@/features/Notifications/show/DeleteNotification";
import { DownloadActions } from "@/features/Notifications/show/DownloadActions";
import { EmailActions } from "@/features/Notifications/show/EmailActions";
import { NotificationMetadata } from "@/features/Notifications/show/NotificationMetadata";
import { PrintActions } from "@/features/Notifications/show/PrintActions";
import { Notification, NotificationQuery } from "@/features/Notifications/show/queries";
import { SmsActions } from "@/features/Notifications/show/SmsActions";
import { SmsView } from "@/features/Notifications/SmsView";
import { NotifierEnum } from "@/generated/gql/graphql";
import { NotificationTransmissions } from "@/features/Notifications/show/NotificationTransmissions";

export const Route = createFileRoute(
  "/$organizationKey/$dossierId/notifications/show/$notificationId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { data } = useSuspenseQuery(NotificationQuery, { variables: params });
  const notification = data.dossier.notification;

  return (
    <div>
      <PageTitle title="notificationDetails" />
      <Section variant="header">
        <SectionHeader
          header={notification.name}
          buttons={
            <>
              <BackButton />
              <DeleteNotification notificationId={notification.id} />
            </>
          }
        />
        <NotificationMetadata notification={notification} />
      </Section>
      <Section>
        <ContentSection notification={notification} />
      </Section>
    </div>
  );
}

const ContentSection = ({ notification }: { notification: Notification }) => {
  const notifier = notifierFor(notification);

  if (notifier === NotifierEnum.Email) {
    return (
      <div className="grid-cols-3 gap-4 md:grid">
        <div className="flex flex-col gap-4 md:order-last">
          <NotificationTransmissions notification={notification} />
          <EmailActions notification={notification} />
          <DownloadActions notification={notification} />
          <PrintActions notification={notification} />
        </div>

        <div className="col-span-2">
          <EmailView to={notification.respondent.email ?? "-"} subject={notification.subject ?? ""}>
            <RenderedDocument
              dangerousHTML={notification.content}
              disableInputs
              documentVariables={{}}
              onDocumentVariableChange={() => null}
            />
          </EmailView>
        </div>
      </div>
    );
  } else if (notifier === NotifierEnum.Sms) {
    return (
      <div className="grid-cols-3 gap-4 md:grid">
        <div className="flex flex-col gap-4 md:order-last">
          <SmsActions notification={notification} />
        </div>

        <div className="col-span-2">
          <SmsView>
            <RenderedDocument
              dangerousHTML={notification.content}
              disableInputs
              documentVariables={{}}
              onDocumentVariableChange={() => null}
            />
          </SmsView>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

function notifierFor(notification: Notification) {
  if (notification.notifiedBy) {
    return notification.notifiedBy;
  }

  if (notification.notifyBy.length > 0) {
    if (notification.notifyBy.includes(NotifierEnum.Email)) {
      return NotifierEnum.Email;
    } else if (notification.notifyBy.includes(NotifierEnum.Sms)) {
      return NotifierEnum.Sms;
    } else {
      return null;
    }
  }

  return NotifierEnum.Email;
}
